import { MapPin, Building, Home, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MainLayout from "@/components/layouts";
import PropertyAppointmentsTable from "@/app/pmc/properties-detail/properties-detail.components/property-appointments-table";
import { usePropertyDetail } from "@/app/pmc/properties-detail/properties-detail.hooks.ts";
import { useState } from "react";
import { useParams } from "react-router";
import { uuid } from "@/interfaces";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { getInitials } from "@/lib/utils.ts";
import { UpdatePropertyModal } from "@/app/pmc/properties-detail/properties-detail.components/update-property-modal.tsx";
import { Image } from "@/components/shared/image-fallback";
import { Skeleton } from "@/components/ui/skeleton";
import { PropertyUnitsTab } from "@/app/pmc/properties-detail/properties-detail.components/property-units/property-units-tab.tsx";
import { PropertyUsersTab } from "@/app/pmc/properties-detail/properties-detail.components/property-users/property-users-tab.tsx";

export default function PropertyDetail() {
  const { propertyId } = useParams<{ propertyId: uuid }>();
  const { handleDelete, loading, deleting, property, notFound, error } = usePropertyDetail(propertyId);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const loadingBreadcrumb = [
    {
      name: "Properties",
      href: "/properties",
    },
    {
      name: "Loading...",
      href: "#",
    },
  ];

  if (!property && !loading && (notFound || error))
    return (
      <MainLayout rightSidebar={false} breadcrumb={[{ name: "Properties", href: "/properties" }]}>
        <p className="text-destructive">{notFound ? "Property not found" : error}</p>
      </MainLayout>
    );

  if (!property || loading)
    return (
      <MainLayout rightSidebar={false} breadcrumb={loadingBreadcrumb}>
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-9 space-y-6">
              <Skeleton className="h-96 w-full rounded-lg" />
              <Skeleton className="h-[300px] w-full rounded-lg" />
            </div>
            <div className="lg:col-span-3 space-y-6">
              <Skeleton className="h-48 w-full rounded-lg" />
              <Skeleton className="h-48 w-full rounded-lg" />
            </div>
          </div>
        </div>
      </MainLayout>
    );

  // TODO: SYN-66 assign user as primary contact
  const propertyManager = property.property_manager;

  const breadcrumb = [
    {
      name: "Properties",
      href: "/properties",
    },
    {
      name: property.property.name,
      href: "#",
    },
  ];

  return (
    <MainLayout rightSidebar={false} breadcrumb={breadcrumb}>
      <div className="container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-9 space-y-6">
            {/* Image Gallery */}
            <Card className="overflow-hidden p-0">
              <div className="relative">
                <Image src={property.property.image as string} alt={property.property.name} />

                <div className="absolute top-4 left-4">
                  <Badge className="bg-white text-black font-semibold">
                    Class {property.property.class?.join(", ")}
                  </Badge>
                </div>
              </div>
            </Card>

            {/* Property Information */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl mb-2">{property.property.name}</CardTitle>
                    <div className="flex items-center text-gray-600 mb-4">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span>{property.property.address}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {property.property.type?.map((t) => (
                    <Badge key={t} variant="secondary" className="flex items-center gap-1">
                      <Building className="h-3 w-3" />
                      {t}
                    </Badge>
                  ))}

                  {property.property.structure?.map((s) => (
                    <Badge key={s} variant="secondary" className="flex items-center gap-1">
                      <Home className="h-3 w-3" />
                      {s}
                    </Badge>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="units">Units</TabsTrigger>
                    <TabsTrigger value="appointments">Appointments</TabsTrigger>
                    <TabsTrigger value="users">Contacts</TabsTrigger>
                    <TabsTrigger value="location">Location</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="mt-4">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold mb-2">Description</h3>
                        <p className="text-muted-foreground leading-relaxed">{property.property.description}</p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-muted rounded-lg">
                          <div className="font-semibold text-lg">{property.unit_count}</div>
                          <div className="text-sm text-muted-foreground">Total Units</div>
                        </div>
                        <div className="text-center p-3 bg-muted rounded-lg">
                          <div className="font-semibold text-lg">{property.property.floors}</div>
                          <div className="text-sm text-muted-foreground">Floors</div>
                        </div>
                        <div className="text-center p-3 bg-muted rounded-lg">
                          <div className="font-semibold text-lg">{property.appointmentCount}</div>
                          <div className="text-sm text-muted-foreground">Bookings</div>
                        </div>
                        <div className="text-center p-3 bg-muted rounded-lg">
                          <div className="font-semibold text-lg">{property.property.year_built}</div>
                          <div className="text-sm text-muted-foreground">Year Built</div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="units" className="mt-4">
                    <PropertyUnitsTab propertyId={property.property.id} />
                  </TabsContent>

                  <TabsContent value="appointments" className="mt-4">
                    <div className="space-y-4">
                      <PropertyAppointmentsTable propertyId={property.property.id} />
                    </div>
                  </TabsContent>

                  <TabsContent value="users" className="mt-4">
                    <PropertyUsersTab propertyId={property.property.id} />
                  </TabsContent>

                  <TabsContent value="location" className="mt-4">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">Full Address</h4>
                        <p className="text-gray-600">{property.property.address}</p>
                      </div>
                      <div className="google-map-code">
                        <iframe
                          src={`https://www.google.com/maps?q=${property.property.latitude},${property.property.longitude}&z=16&output=embed`}
                          style={{ border: "0", width: "100%", height: 400 }}
                          loading="lazy"
                        ></iframe>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 lg:col-span-3 ">
            <div className="sticky top-20">
              {/* Property Manager */}
              {propertyManager && (
                <Card className="mb-6">
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={propertyManager.image || "/placeholder.svg"} />
                        <AvatarFallback>{getInitials(propertyManager.name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-semibold">{propertyManager.name}</h3>
                        <p className="text-sm text-gray-600">{propertyManager.description}</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Button variant="ghost" className="w-full justify-start" size="sm">
                        <Phone className="h-4 w-4 mr-2" />
                        <span className="text-xs font-regular">{propertyManager.phone}</span>
                      </Button>
                      <Button variant="ghost" className="w-full justify-start" size="sm">
                        <Mail className="h-4 w-4 mr-2" />
                        <span className="text-xs font-regular">{propertyManager.email}</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Actions */}
              <Card>
                <CardContent className="space-y-3">
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                  <Button className="w-full">Book An Appointment</Button>
                  <UpdatePropertyModal property={property} />
                  <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                    <DialogTrigger asChild>
                      <Button variant="destructive" className="w-full">
                        Delete Property
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Are you sure?</DialogTitle>
                      </DialogHeader>
                      <p>
                        This action cannot be undone. This will permanently delete the property and associated data.
                      </p>
                      <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>
                          Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                          {deleting ? "Deleting..." : "Delete"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
